import { useEffect, useCallback } from 'react';
import {
  ref,
  onValue,
  set,
  push,
  update,
  remove,
  onDisconnect,
  serverTimestamp,
} from 'firebase/database';
import { db } from '../config/firebase';
import { useTimerStore } from '../stores/timerStore';
import type { Timer, TimerType, TimerAction, PlayedGameEntry } from '../types';
import { generateId, generateRoomCode } from '../utils/generateId';
import { formatTime, formatDuration } from '../utils/formatTime';
import { getServerTime } from '../utils/serverTime';

/**
 * Hook that manages Firebase Realtime Database synchronization for a room.
 * Handles listeners, writes, and presence.
 */
export function useFirebaseSync(roomId: string | null) {
  const {
    user,
    setTimers,
    setHistory,
    setActivityLog,
    setParticipants,
    setCurrentRoom,
    setIsConnected,
    addRecentRoom,
  } = useTimerStore();

  // Listen for room data changes
  useEffect(() => {
    if (!roomId) return;

    // 1. Listen to room metadata without triggering presence writes inside the listener
    const roomRef = ref(db, `rooms/${roomId}`);
    const unsubRoom = onValue(roomRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setCurrentRoom({
          id: roomId,
          name: data.name || 'Salle',
          code: data.code || '',
          createdAt: data.createdAt || Date.now(),
          createdBy: data.createdBy || 'Anonyme',
          timers: data.timers || {},
          participants: data.participants || {},
          history: data.history || {},
          activityLog: data.activityLog || {},
        });
        addRecentRoom({ id: roomId, name: data.name || 'Salle', code: data.code || '' });
      } else {
        setCurrentRoom(null);
      }
    });

    // 2. Set up presence ONLY on connection state change (isolated from room writes!)
    const connectedRef = ref(db, '.info/connected');
    const unsubConnected = onValue(connectedRef, (snapshot) => {
      const isConnectedVal = snapshot.val() === true;
      setIsConnected(isConnectedVal);

      if (isConnectedVal && user?.pseudo && roomId) {
        const presenceRef = ref(db, `rooms/${roomId}/participants/${user.pseudo}`);
        set(presenceRef, {
          pseudo: user.pseudo,
          color: user.color || '#00e5ff',
          online: true,
          lastSeen: Date.now(),
        });

        onDisconnect(presenceRef).update({
          online: false,
          lastSeen: serverTimestamp(),
        });
      }
    });

    // Listen to timers
    const timersRef = ref(db, `rooms/${roomId}/timers`);
    const unsubTimers = onValue(timersRef, (snapshot) => {
      setTimers(snapshot.val() || {});
    });

    // Listen to history
    const historyRef = ref(db, `rooms/${roomId}/history`);
    const unsubHistory = onValue(historyRef, (snapshot) => {
      setHistory(snapshot.val() || {});
    });

    // Listen to activity log
    const activityRef = ref(db, `rooms/${roomId}/activityLog`);
    const unsubActivity = onValue(activityRef, (snapshot) => {
      setActivityLog(snapshot.val() || {});
    });

    // Listen to participants
    const participantsRef = ref(db, `rooms/${roomId}/participants`);
    const unsubParticipants = onValue(participantsRef, (snapshot) => {
      setParticipants(snapshot.val() || {});
    });

    return () => {
      unsubRoom();
      unsubConnected();
      unsubTimers();
      unsubHistory();
      unsubActivity();
      unsubParticipants();
    };
  }, [roomId, user?.pseudo, user?.color, setTimers, setHistory, setActivityLog, setParticipants, setCurrentRoom, setIsConnected, addRecentRoom]);

  // === Room Actions ===

  const createRoom = useCallback(async (name: string): Promise<string> => {
    const roomId = generateId();
    const code = generateRoomCode();
    const roomRef = ref(db, `rooms/${roomId}`);
    await set(roomRef, {
      name,
      createdAt: Date.now(),
      createdBy: user?.pseudo || 'Anonyme',
      code,
      timers: {},
      participants: {},
      history: {},
    });
    return roomId;
  }, [user]);

  const deleteRoom = useCallback(async () => {
    if (!roomId) return;
    await remove(ref(db, `rooms/${roomId}`));
  }, [roomId]);

  // === Activity Logging Helper ===

  const logActivity = useCallback(
    async (
      timerId: string,
      timerName: string,
      action: TimerAction,
      details?: string
    ) => {
      if (!roomId || !user) return;
      const activityRef = ref(db, `rooms/${roomId}/activityLog`);
      const newActivityRef = push(activityRef);
      await set(newActivityRef, {
        timerId,
        timerName,
        action,
        user: user.pseudo,
        userColor: user.color,
        timestamp: Date.now(),
        details: details || null,
      });
    },
    [roomId, user]
  );

  // === Timer Actions ===

  const createTimer = useCallback(async (name: string, type: TimerType, countdownDurationMs?: number) => {
    if (!roomId || !user) return;
    const timersRef = ref(db, `rooms/${roomId}/timers`);
    const newTimerRef = push(timersRef);
    await set(newTimerRef, {
      name,
      type,
      state: 'idle',
      startedAt: null,
      accumulatedMs: 0,
      countdownDurationMs: type === 'countdown' ? (countdownDurationMs || 0) : null,
      createdBy: user.pseudo,
      createdAt: Date.now(),
      lastUpdatedBy: user.pseudo,
      order: Date.now(),
    });
    await logActivity(
      newTimerRef.key || '',
      name,
      'create',
      type === 'countdown' ? `durée: ${formatDuration(countdownDurationMs || 0)}` : undefined
    );
  }, [roomId, user, logActivity]);

  const startTimer = useCallback(async (timerId: string) => {
    if (!roomId || !user) return;
    const timer = useTimerStore.getState().timers[timerId];
    const timerRef = ref(db, `rooms/${roomId}/timers/${timerId}`);
    await update(timerRef, {
      state: 'running',
      startedAt: getServerTime(),
      lastUpdatedBy: user.pseudo,
    });
    await logActivity(timerId, timer?.name || 'Chronomètre', 'start');
  }, [roomId, user, logActivity]);

  const pauseTimer = useCallback(async (timerId: string, currentAccumulatedMs: number) => {
    if (!roomId || !user) return;
    const timer = useTimerStore.getState().timers[timerId];
    const timerRef = ref(db, `rooms/${roomId}/timers/${timerId}`);
    await update(timerRef, {
      state: 'paused',
      startedAt: null,
      accumulatedMs: currentAccumulatedMs,
      lastUpdatedBy: user.pseudo,
    });
    await logActivity(timerId, timer?.name || 'Chronomètre', 'pause', `à ${formatTime(currentAccumulatedMs)}`);
  }, [roomId, user, logActivity]);

  const stopTimer = useCallback(async (timerId: string, timer: Timer, totalElapsedMs: number) => {
    if (!roomId || !user) return;

    // Normalize played games list if tracked
    let playedGamesList: PlayedGameEntry[] = [];
    if (timer.playedGames) {
      playedGamesList = Array.isArray(timer.playedGames)
        ? [...timer.playedGames]
        : (Object.values(timer.playedGames) as PlayedGameEntry[]);
    }

    // Include the currently active game if any
    if (timer.activeGame && timer.activeGame.detectedAt) {
      const activeDuration = Math.max(0, getServerTime() - timer.activeGame.detectedAt);
      playedGamesList.push({
        game: timer.activeGame.friendlyName || timer.activeGame.windowTitle,
        exeName: timer.activeGame.exeName,
        startedAt: timer.activeGame.detectedAt,
        endedAt: getServerTime(),
        durationMs: activeDuration,
      });
    }

    // Update timer state to idle (clean reset ready for next session, no red alert)
    const timerRef = ref(db, `rooms/${roomId}/timers/${timerId}`);
    await update(timerRef, {
      state: 'idle',
      startedAt: null,
      accumulatedMs: 0,
      lastUpdatedBy: user.pseudo,
      activeGame: null,
      playedGames: null,
    });

    // Record in history
    const historyRef = ref(db, `rooms/${roomId}/history`);
    const newHistoryRef = push(historyRef);
    await set(newHistoryRef, {
      timerName: timer.name,
      type: timer.type,
      totalDurationMs: totalElapsedMs,
      startedAt: timer.createdAt,
      endedAt: getServerTime(),
      stoppedBy: user.pseudo,
      playedGames: playedGamesList.length > 0 ? playedGamesList : null,
    });

    // Record in activity log
    await logActivity(timerId, timer.name, 'stop', `durée totale: ${formatDuration(totalElapsedMs)}`);
  }, [roomId, user, logActivity]);

  const resetTimer = useCallback(async (timerId: string, currentElapsedMs?: number) => {
    if (!roomId || !user) return;
    const timer = useTimerStore.getState().timers[timerId];
    if (!timer) return;

    let totalElapsed = currentElapsedMs ?? (timer.accumulatedMs || 0);
    if (currentElapsedMs === undefined && timer.state === 'running' && timer.startedAt) {
      totalElapsed += getServerTime() - timer.startedAt;
    }

    // If timer was NOT already finished (i.e. not previously archived by stopTimer),
    // and there is meaningful elapsed time (>= 1000ms) or games were detected, archive it!
    if (timer.state !== 'finished' && (totalElapsed >= 1000 || timer.playedGames || timer.activeGame)) {
      let playedGamesList: PlayedGameEntry[] = [];
      if (timer.playedGames) {
        playedGamesList = Array.isArray(timer.playedGames)
          ? [...timer.playedGames]
          : (Object.values(timer.playedGames) as PlayedGameEntry[]);
      }

      if (timer.activeGame && timer.activeGame.detectedAt) {
        const activeDuration = Math.max(0, getServerTime() - timer.activeGame.detectedAt);
        playedGamesList.push({
          game: timer.activeGame.friendlyName || timer.activeGame.windowTitle,
          exeName: timer.activeGame.exeName,
          startedAt: timer.activeGame.detectedAt,
          endedAt: getServerTime(),
          durationMs: activeDuration,
        });
      }

      // Record in history
      const historyRef = ref(db, `rooms/${roomId}/history`);
      const newHistoryRef = push(historyRef);
      await set(newHistoryRef, {
        timerName: timer.name,
        type: timer.type,
        totalDurationMs: totalElapsed,
        startedAt: timer.createdAt,
        endedAt: getServerTime(),
        stoppedBy: user.pseudo,
        playedGames: playedGamesList.length > 0 ? playedGamesList : null,
      });

      await logActivity(timerId, timer.name, 'stop', `archivé lors du reset (${formatDuration(totalElapsed)})`);
    }

    // Reset timer to idle
    const timerRef = ref(db, `rooms/${roomId}/timers/${timerId}`);
    await update(timerRef, {
      state: 'idle',
      startedAt: null,
      accumulatedMs: 0,
      lastUpdatedBy: user.pseudo,
      playedGames: null,
      activeGame: null,
    });
    await logActivity(timerId, timer.name || 'Chronomètre', 'reset');
  }, [roomId, user, logActivity]);

  const renameTimer = useCallback(async (timerId: string, newName: string) => {
    if (!roomId || !user) return;
    const timer = useTimerStore.getState().timers[timerId];
    const timerRef = ref(db, `rooms/${roomId}/timers/${timerId}`);
    await update(timerRef, {
      name: newName,
      lastUpdatedBy: user.pseudo,
    });
    await logActivity(timerId, newName, 'rename', `ancien nom: "${timer?.name}"`);
  }, [roomId, user, logActivity]);

  const deleteTimer = useCallback(async (timerId: string) => {
    if (!roomId) return;
    const timer = useTimerStore.getState().timers[timerId];
    await remove(ref(db, `rooms/${roomId}/timers/${timerId}`));
    await logActivity(timerId, timer?.name || 'Chronomètre', 'delete');
  }, [roomId, logActivity]);

  const toggleFreePlay = useCallback(async (timerId: string, enabled: boolean) => {
    if (!roomId || !user) return;
    const timer = useTimerStore.getState().timers[timerId];
    const timerRef = ref(db, `rooms/${roomId}/timers/${timerId}`);
    await update(timerRef, {
      freePlayMode: enabled,
      lastUpdatedBy: user.pseudo,
    });
    await logActivity(
      timerId,
      timer?.name || 'Chronomètre',
      'toggle_free_play',
      enabled ? 'Mode roue libre (bénévole) activé' : 'Mode roue libre désactivé'
    );
  }, [roomId, user, logActivity]);

  // === History & Activity Actions ===

  const deleteHistoryEntry = useCallback(async (historyId: string) => {
    if (!roomId) return;
    await remove(ref(db, `rooms/${roomId}/history/${historyId}`));
  }, [roomId]);

  const clearHistory = useCallback(async () => {
    if (!roomId) return;
    await remove(ref(db, `rooms/${roomId}/history`));
  }, [roomId]);

  const clearActivityLog = useCallback(async () => {
    if (!roomId) return;
    await remove(ref(db, `rooms/${roomId}/activityLog`));
  }, [roomId]);

  return {
    createRoom,
    deleteRoom,
    createTimer,
    startTimer,
    pauseTimer,
    stopTimer,
    resetTimer,
    renameTimer,
    deleteTimer,
    toggleFreePlay,
    deleteHistoryEntry,
    clearHistory,
    clearActivityLog,
  };
}

/**
 * Hook to find a room by its short code.
 */
export function useFindRoomByCode() {
  const findRoom = useCallback(async (code: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const roomsRef = ref(db, 'rooms');
      onValue(roomsRef, (snapshot) => {
        if (!snapshot.exists()) {
          resolve(null);
          return;
        }
        const rooms = snapshot.val();
        for (const [roomId, roomData] of Object.entries(rooms)) {
          if ((roomData as Record<string, unknown>).code === code.toUpperCase()) {
            resolve(roomId);
            return;
          }
        }
        resolve(null);
      }, { onlyOnce: true });
    });
  }, []);

  return { findRoom };
}
